import { formatDistanceToNow, parseISO } from "date-fns";
import * as Sentry from "@sentry/browser";
import ApiService from "@/common/api.service";

export function getParameters() {
  return JSON.parse(document.querySelector("html").dataset.userParameters);
}

export function makeAPIURLsRelative(obj, host, ignoreKeys) {
  /*
   * Strips the hostname from API URLs in a JSON object, making them relative.
   *
   * Useful for comparing relative API URLs generated within backend code to URLs
   * returned by API requests.
   *
   * Note: modification is currently done in-place.
   *
   * Arguments:
   *   obj: The JSON object to recursovely search for API URLs
   *   host: Optional. The search string to determine if something if an API URL.
   *     If not specified, an attempt will be made to determine it from the
   *     top-level "url" key of the JSON object.
   *   ignoreKeys: A list of keys to not modify. keys can be at any depth, and will
   *     be checked at all depths.
   *
   * Returns:
   *   The JSON object with the hostname recursively removed from any detected API
   *   URLs.
   */
  if (ignoreKeys === undefined) {
    ignoreKeys = [];
  }
  if (!host && obj.url) {
    // If not hostname provided and the object has a URL field, determine the
    // search string by parsing the JSON object's "url" field. Note we include
    // "/api/" in order to only identify API URLs in our search.
    let url = new URL(obj.url);
    host =
      url.protocol + "//" + url.hostname + (url.port ? ":" + url.port : "") + "/api/";
  }
  // For each field in the returned JSON
  for (const key in obj) {
    // If it's the "url" field or another string field that starts with the API
    // URL search string, extract from the field value, only the pathname of the
    // URL. This will include the "/api/" in the resultant string since we are
    // not using the search string to find/replace, only match.
    // Note that if obj is a list of URLs (e.g. an unexpanded m2m relation), this
    // will catch it when it's recursively passed to this function (in which case
    // the keys will be list indices).
    // Note: If a url field starts with "/" it's considered already relative.
    if (ignoreKeys.includes(key)) {
      continue;
    } else if (
      (key == "url" && obj[key] && !obj[key].startsWith("/")) ||
      (obj[key] && typeof obj[key] === "string" && obj[key].startsWith(host))
    ) {
      obj[key] = new URL(obj[key]).pathname;
    } else if (obj[key] && typeof obj[key] === "object") {
      // If it's an object (list or dict), recursively process the contents
      makeAPIURLsRelative(obj[key], host, ignoreKeys);
    }
  }
  return obj;
}

export function getQueryArgsFromNextURL(url) {
  // Parse the URL
  let nextURL = new URL(url);
  let queryArgs = {};
  // Iterate over the query args (so that we don't lose any duplicates)
  for (const [key, value] of nextURL.searchParams.entries()) {
    // Check if we already have a value for this key
    if (key in queryArgs) {
      // If this entry is not already stored as an array, convert it as this
      // is the format expected by drf-serializer-extensions
      if (!Array.isArray(queryArgs[key])) {
        queryArgs[key] = [queryArgs[key]];
      }
      // Add new value to the array
      queryArgs[key].push(value);
    } else {
      // Add it as just a key:value pair
      queryArgs[key] = value;
    }
  }
  return queryArgs;
}

export function getLanguageCode() {
  return document.documentElement.lang;
}

export function getTimeZone() {
  return document.documentElement.dataset?.timeZone;
}

/**
 * Returns the full URL for a Django static file, using the `data-static-url` attribute
 * from the `<html>` element.
 *
 * This is the JS equivalent of Django's `{% static %}` template tag.
 *
 * @param {String} path - The path to the static file, relative to the static root.
 * @returns {String} The full URL for the static file.
 */
export function getStaticUrl(path) {
  return `${document.documentElement.dataset.staticUrl}${path}`;
}

/**
 * Formats a date string into a locale-specific date representation.
 *
 * @param {String} date_string - The date string in ISO format.
 * @param {Object} options - Options for formatting the date.
 * @param {Object} options.formatOptions - Options to pass to `toLocaleDateString()`.
 * Defaults to `{ day: "numeric", month: "numeric", year: "numeric" }`
 * @returns {String} - The formatted date string.
 */
export function localeDate(
  date_string,
  { formatOptions = { day: "numeric", month: "numeric", year: "numeric" } } = {},
) {
  let date = parseISO(date_string);
  return date.toLocaleDateString(getLanguageCode(), formatOptions);
}

export function niceDateTime(api_datetime_string, { daysBeforeAbsolute = null } = {}) {
  let date = parseISO(api_datetime_string);
  let current_date = new Date();
  let difference_days = (current_date - date) / 1000 / 60 / 60 / 24;
  if (daysBeforeAbsolute != null && difference_days > daysBeforeAbsolute) {
    return date.toLocaleString(getLanguageCode(), {
      timeZone: getTimeZone(),
      timeZoneName: "short",
    });
  } else {
    return `${formatDistanceToNow(date)} ago`;
  }
}

export function isDeepEmpty(obj) {
  // Returns whether the nested object contains only empty objects
  // Note: Will crash for Maps.
  if (typeof obj === "object" && obj !== null) {
    for (const key in obj) {
      if (!isDeepEmpty(obj[key])) {
        return false;
      }
    }
    return true;
  }

  // Empty objects/arrays will be caught by the above.
  return obj === null;
}

/**
 * Configure print buttons to open the print preview dialog
 */
export function configurePrintButtonEvents() {
  for (const el of document.querySelectorAll(".print-trigger")) {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      window.print();
    });
  }
}

/**
 * Calls a function when the DOM content has loaded.
 *
 * If the DOM content has already loaded, the function is called immediately.
 *
 * @param {(input: any) => null} fn - The function to call
 */
export function runOnDOMContentLoaded(fn) {
  /*
   * Note, per MDN, there is no race condition here. It's not possible for the document
   * to be loaded between the if check and the addEventListener() call. JavaScript has
   * run-to-completion semantics, which means if the document is loading at one
   * particular tick of the event loop, it can't become loaded until the next cycle, at
   * which time the doSomething handler is already attached and will be fired.
   */
  if (document.readyState == "loading") {
    document.addEventListener("DOMContentLoaded", fn);
  } else {
    fn();
  }
}

/**
 * A helped function to generate a dictionary that clears common Highcharts style
 * options so that CSS classes take precedence.
 *
 * This is useful if you want to disable Highcharts `styledMode`, but want some aspects
 * to still use the CSS classes we have defined.
 *
 * @returns {Object} An object with common style options set to null
 */
export function clearHighchartsDefaultStyles() {
  return {
    // Clear the defaults added by Highcharts. We have applied styles via CSS in
    // `generic-highcharts.scss`
    fontFamily: null,
    fontWeight: null,
    fontSize: null,
    fill: null,
    color: null,
  };
}

/**
 * A function that returns the value of a nested field in a object, from a string
 * containing a dotted path value.
 *
 * Assumes that keys inside objects do not contain dots.
 *
 * @param {Object} data - The object to extract the value from.
 * @param {String} path - The dotted path to the value to extract.
 * @returns {*} - The value at the dotted path in the object. If the path does not
 * exist, `null` is returned.
 */
export function getDataFromDottedPath(data, path) {
  return path
    .split(".")
    .reduce((obj, key) => (obj && obj.hasOwnProperty(key) ? obj[key] : null), data);
}

/**
 * A helper function to determine if a page number is valid.
 *
 * @param {number} page - The page number to check
 * @returns {boolean} Whether the page number is valid
 */
export function validPageNumber(page) {
  return page && !isNaN(page) && page > 0 && page !== null;
}

/**
 * A helper function to determine if a id number is valid.
 *
 * @param {number} id - The id number to check
 * @returns {boolean} Whether the id number is valid
 */
export function validResourceId(id) {
  return id && (typeof id === "string" || (!isNaN(id) && id > 0)) && id !== null;
}

/**
 * A sort function for comparing lists of integers
 *
 * This is used to determine which list comes before the other list (not to be confused
 * with sorting the list itself).
 *
 * Useful for situations like:
 * - Sorting groups of items where each item has an order. For example, does the group
 *   of academic years "Year 2, Year 5" (represented as [2, 5]) come before or after the
 *   group of academic years "Year 2, Year 6" (represented as [2, 6])?
 * - Sorting a list of items that are displayed hierarchically (with each level in the
 *   hierarchy having an order) but are currently stored in a flat list. For
 *   example,sorting a flat list of curriculum nodes where each node's order includes
 *   the order of the parent nodes (see ``CurriculumNode.inheritedOrder`` for an
 *   example).
 *
 * Usage:
 * ```javascript
 * const item1 = [2, 5];
 * const item2 = [2, 6];
 * const item3 = [1, 5];
 * const item4 = [1];
 * const items = [item1, item2, item3, item4];
 *
 * // Result: [item4, item3, item1, item2]
 * items.sort(compareListElements);
 * ```
 *
 * @param {Array} a - The first list to compare
 * @param {Array} b - The second list to compare
 * @returns {number} - A negative number if a comes before b, a positive number if b
 * comes before a, and 0 if they are equal.
 */
export function compareListElements(a, b) {
  for (let i = 0; i < a.length && i < b.length; i++) {
    if (a[i] !== b[i]) {
      return a[i] - b[i];
    }
  }
  // If the orders are the same up to the length of the shorter order, the shorter
  // order comes first
  return a.length - b.length;
}

/**
 * Fetch all items in every page for given url entity in database
 *
 * If you need to retrieve all items from a database entity and they are paginated, this
 * will cycle through all pages and retrieve the whole list of desired items at the
 * maximum per-page amount. It also handles errors for edge cases related to both the
 * recursive nature of the function and the possibility that items may be manipulated
 * while the api is being queried.
 *
 * @param {String} apiUrl - The API endpoint to query (relative to the `ApiService`
 * base URL).
 * @param {Object} options Additional options for the query.
 * @param {Object} options.apiQueryArgs An object containing additional parameters to
 * include in the API call as query arguments. These should be refs if the query should
 * be reactive to changes in them.
 * @param {String} options.itemsKey - Allows for flexibility of bracket notation in accessing
 * the returned lists items
 * @param {String} options.pkKey - Allows for flexibility of bracket notation in accessing
 * the returned lists items primary keys
 * @param {Number} options.perPage - The number of items to be returned in the list for each
 * page, set to maximum since we want all items
 * @param {Object} options.apiServiceConfig - An object that holds axios api configuration options
 * @returns {Object} An object with the collated items and their count
 */
export async function fetchAllPages(
  apiUrl,
  {
    apiQueryArgs = {},
    itemsKey = "results",
    pkKey = "url",
    perPage = 250,
    apiServiceConfig = null,
  } = {},
  { page = 1, _result = null } = {},
) {
  if ((page != 1 && _result === null) || (_result !== null && page == 1)) {
    const error = new Error("fetchAllPages called with incorrect arguments");
    Sentry.captureException(error, { extra: { apiUrl, page } });
    throw error;
  }

  const { data } = await ApiService.query(
    apiUrl,
    {
      page: page,
      limit: perPage,
      ...apiQueryArgs,
    },
    apiServiceConfig,
  );

  if (page == 1) {
    _result = data;
  } else {
    _result[itemsKey] = _result[itemsKey].concat(data[itemsKey]);
    // Make sure the number of items didn't change between pages. If it did, we have no
    // way of knowing whether we missed an item or got a duplicate item. SO we throw an
    // error.
    if (_result.count != data.count) {
      const error = new Error("Data changed during multi-page fetch");
      Sentry.captureException(error, { extra: { apiUrl, page } });
      throw error;
    }
  }

  if (_result[itemsKey].length === _result.count) {
    const ids = new Set(_result[itemsKey].map((item) => item[pkKey]));
    // Make sure we didn't get any duplicate entries. If we did, that likely means we
    // had a deletion and addition between pages (such that the total count stayed
    // constant). However, the presence of a duplicate implies we missed one, so we need
    // to throw an error.
    if (ids.size !== _result[itemsKey].length) {
      const error = new Error("Duplicate items fetched during multi-page fetch");
      Sentry.captureException(error, { extra: { apiUrl, page } });
      throw error;
    }
    return _result;
  }

  return fetchAllPages(
    apiUrl,
    {
      apiQueryArgs,
      itemsKey,
      perPage,
      apiServiceConfig,
    },
    {
      page: page + 1,
      _result,
    },
  );
}

let _scrollBarWidth = null;

/** Compute the width of a vertical scroll bar
 *
 * This function caches the result and returns it on subsequent calls by default. The
 * cache can be ignored by providing ``invalidateCache: true``.
 *
 * Note that this is not guaranteed to succeed in all browsers, or all OS configurations
 * (e.g. touch devices that may not always show a scrollbar). On macOS devices using a
 * trackpad input, this function is expected to return ``0`` if macOS is configured to
 * hide scrollbars except when scrolling.
 *
 * @param {Object} options - Options for the function
 * @param {boolean} options.invalidateCache - If true, the cached scrollbar width will
 * be invalidated and recalculated. Use this if you suspect the scrollbar width has
 * changed since a previous call to the function.
 * @returns {number} The width of the vertical scrollbar in pixels
 */
export function getScrollBarWidth({ invalidateCache = false } = {}) {
  // If we have already calculated the scrollbar width, return it
  if (_scrollBarWidth && !invalidateCache) {
    return _scrollBarWidth;
  }

  // Create a temporary element to measure the scrollbar width
  const outer = document.createElement("div");
  outer.style.visibility = "hidden";
  outer.style.overflow = "scroll";
  outer.style.width = "300px";
  outer.style.height = "300px";
  document.body.appendChild(outer);

  const inner = document.createElement("div");
  inner.style.width = "100%";
  inner.style.height = "500px";
  outer.appendChild(inner);

  try {
    // Calculate the scrollbar width. This is trying a few different ways in the hope of
    // capturing most of the variations between browsers, operating systems, and
    // operating system settings.
    _scrollBarWidth = outer.offsetWidth - inner.offsetWidth;
    if (_scrollBarWidth == 0) {
      _scrollBarWidth = outer.offsetWidth - outer.children[0].clientWidth;
    }
  } finally {
    // Remove the temporary elements from the DOM
    outer.parentNode.removeChild(outer);
  }

  return _scrollBarWidth;
}

/**
 * Returns a shallow copy of an object with specified keys removed.
 *
 * @param {Object} obj - The original object
 * @param {Array<String>|String} keysToRemove - An array of keys to remove from the object
 * @return {Object} A new object with the specified keys removed
 */
export function filterObjectProps(obj, keysToRemove) {
  if (!Array.isArray(keysToRemove)) {
    keysToRemove = [keysToRemove];
  }
  return Object.fromEntries(
    Object.entries(obj).filter(([key, _]) => !keysToRemove.includes(key)),
  );
}

/**
 * Returns a shallow copy of an object with `undefined` values removed.
 *
 * @param {Object} obj - The original object
 * @return {Object} A new object with `undefined` values removed
 */
export function filterUndefinedValues(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined),
  );
}

/**
 * Returns the singular or plural form of a word for a given count.
 *
 * @param {Number} count - The count that selects the form
 * @param {String} one - The singular form
 * @param {String} [many] - The plural form, defaulting to `one` + "s"
 * @return {String} The form that matches the count
 */
export function plural(count, one, many = `${one}s`) {
  return count === 1 ? one : many;
}
